import { AgGridReact } from "ag-grid-react";
import { AxiosResponse } from "axios";
import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { ColumnsToolPanelModule, FiltersToolPanelModule, SetFilterModule } from "ag-grid-enterprise";

import useFetch from "../../../../hooks/useFetchMSAL";
import { TradeAndPromotionPlanningFetchAPI } from "../../../../models/APIResponses.model";
import ForecastReview from "../../../../models/TradeAndPromoPlanning/ForecastReview.model";
import { ForecastReviewDropdownModel, ForecastReviewSelectedSlicerModel } from "../../../../models/TradeAndPromoPlanning/ForecastReviewSlicer.model";
import { AG_GRID_DEFAULT_COL_DEF, AG_GRID_MODULES, AG_GRID_OPTIONS, FORECAST_REVIEW_TAB_ID, TOAST_CONTAINER_ID } from "../../../../shared/constants";
import { usaNumberFormatter, getReOrderedAPIData, getSortedUSADateStrList, getToastOptions } from "../../../../shared/functions";
import { RootState } from "../../../../store";
import { forecastReviewTabActions } from "../../../../store/slices/forecast-review-tab-slice";
import { tradeAndPromoPlanningTabActions } from "../../../../store/slices/trade-and-promo-planning-tab-slice";
import "../../../../styles.css";
import ForecastReviewSlicers from "./ForecastReviewSlicers";

const rowLabelsAndIndexes: {
    [key: string]: {
        label: string;
        rowIndex: number;
        editable: boolean;
    }
} = {
    prev_year_shipment_history: {
        label: "Previous Year Shipments",
        rowIndex: 0,
        editable: false
    },
    ibp_stat_forecast: {
        label: "IBP Forecast",
        rowIndex: 1,
        editable: false
    },
    adjusted_baseline_forecast: {
        label: "Adjusted Baseline Forecast",
        rowIndex: 2,
        editable: false
    },
    promotional_lift: {
        label: "Promotional Lift",
        rowIndex: 3,
        editable: false
    },
    final_consumption_forecast: {
        label: "Final Consumption Forecast",
        rowIndex: 4,
        editable: false
    },
    final_shipment_forecast: {
        label: "Final Shipment Forecast",
        rowIndex: 5,
        editable: false
    },
    direct_shipment_forecast: {
        label: "Direct Shipment Forecast",
        rowIndex: 6,
        editable: false
    }
};

const ForecastReviewGrid = (props: {
    gridHeight: string;
    onGridDataChanged: Function;
}) => {
    // States
    const [gridRowData, setGridRowData] = useState<ForecastReview[]>([]);
    const [gridRowDataCopy, setGridRowDataCopy] = useState<ForecastReview[]>([]);
    const [gridColDef, setGridColDef] = useState<any[]>([]);
    const [editedColIds, setEditedColIds] = useState<any[]>([]);
    const [gridCellEdits, setGridCellEdits] = useState<{
        [key: string]: {
            [key: string]: string | number;
        };
    }>({});

    // Variables
    const agGridRef = useRef<AgGridReact>(null);
    const [, fetchData] = useFetch([]);

    // Redux variables
    const dispatchFn = useDispatch();
    const globalCustomer: { id: number; name: string; } = useSelector((state: RootState) => state.tradeAndPromoPlanningTab.globalCustomer);
    const tabApiDataFetched: boolean = useSelector((state: RootState) => state.tradeAndPromoPlanningTab.tabApiDataFetched)[FORECAST_REVIEW_TAB_ID];
    const activeSubTabId: string = useSelector((state: RootState) => state.tradeAndPromoPlanningTab.activeSubTabId);
    const selectedSlicers: ForecastReviewSelectedSlicerModel = useSelector((state: RootState) => state.forecastReviewTab.selectedSlicers);

    /**
     * Returns ColDef for Forecast Review data grid
     * @param {ReplenishmentPlanSummary[]} apiData Data from GET /volume-forecast API
     * @param {PromotionDropdown[]} promotionDropdownData Promotion row dropdown data from GET /volume-forecast API
     * @returns {any[]} Generated ColDef for Forecast Review data grid
     */
    const generateColDef = useCallback((
        apiData: ForecastReview[], 
        // promotionDropdownData: PromotionDropdown[]
    ): any[] => {
        const newColDef: any[] = [
            {
                field: "variable",
                headerTooltip: "Measures",
                cellDataType: "text",
                headerName: "Measures",
                pinned: "left",
                width: 200,
                menuTabs: ["filterMenuTab"],
                filter: "agSetColumnFilter",
                filterParams: {
                    refreshValuesOnOpen: true,
                    valueFormatter: (params: any) => rowLabelsAndIndexes[params.value].label
                },
                valueFormatter: (params: any) => rowLabelsAndIndexes[params.data.variable].label,
                tooltipValueGetter: (params: any) => params.valueFormatted
            }
        ];

        if (apiData && (apiData.length > 0)) {
            const usaDateStrList: string[] = Object.keys(apiData[0]).filter(key => Boolean(Date.parse(key)));
            const sortedUSADateStrList: string[] = getSortedUSADateStrList(usaDateStrList);

            newColDef.push(...sortedUSADateStrList.map((key) => {
                return {
                    field: key,
                    cellDataType: "text",
                    headerName: key,
                    headerTooltip: key,
                    width: 90,
                    cellClass: "ag-right-aligned-cell",
                    headerClass: "ag-left-aligned-cell",
                    editable: (params: any) => (Object.keys(rowLabelsAndIndexes).filter(c => rowLabelsAndIndexes[c].editable).includes(params.data.variable)),
                    valueSetter: (params: any) => {
                        const columnId: string = params.column.colId;

                        if (params.newValue) {
                            const rowLabel: string = rowLabelsAndIndexes[params.node.data.variable].label;

                            if (
                                rowLabel === "IBP Stat Forecast" || 
                                rowLabel === "Adjusted Baseline Forecast" || 
                                rowLabel === "Promotional Lift" || 
                                rowLabel === "Final Consumption Forecast"
                            ) {
                                params.data[columnId] = usaNumberFormatter(parseInt(params.newValue), 2);
                            } else {
                                params.data[columnId] = params.newValue;
                            }

                        } else {
                            params.data[params.column.colId] = null;
                        }
                        return true;
                    },
                    valueFormatter: (params: any): any => {
                        return (typeof params.value === "number") ? usaNumberFormatter(params.value, 0) : params.value
                    },
                    tooltipValueGetter: (params: any) => params.valueFormatted
                };
            }));
        }

        return newColDef;
    }, []);

    /**
     * Re-orders API data according to the required AG Grid row order.
     * @param {ForecastReview[]} fetchApiData API data array fetched from Forecast Review data API
     * @returns {ForecastReview[]} Re-ordered API data array
     */
    const reOrderGridRows = useCallback(
        (fetchApiData: ForecastReview[]): ForecastReview[] => getReOrderedAPIData(rowLabelsAndIndexes, fetchApiData),
        []
    );

    /**
     * Update grid data and slicers from fetch / update API response.
     * @param apiResponse API Response.
     */
    const updateGridDataAndSlicers = useCallback((apiResponse: {
        result: {
            data: ForecastReview[];
        },
        slicers?: ForecastReviewDropdownModel;
    }): void => {
        const apiData: ForecastReview[] = apiResponse.result.data;
        const reOrderedApiData: ForecastReview[] = reOrderGridRows(apiData);

        setGridRowData(structuredClone(reOrderedApiData));
        props.onGridDataChanged(structuredClone(reOrderedApiData));
        setGridRowDataCopy(structuredClone(reOrderedApiData));
        setGridColDef((prevColDef) => generateColDef(reOrderedApiData));

        if (apiResponse.slicers) {
            dispatchFn(forecastReviewTabActions.setSlicers(structuredClone(apiResponse.slicers)));
        }
    }, [props, dispatchFn, generateColDef, reOrderGridRows]);

    /**
     * Async function to fetch data from GET /volume-forecast
     */
    const fetchForecastReviewData = useCallback(async (): Promise<void> => {
        const fetchingDataToastID = toast.loading('Fetching Forecast Review data...', {
            containerId: TOAST_CONTAINER_ID,
            ...getToastOptions('loading')
        });

        try {
            const fetchAPIResponse: AxiosResponse<TradeAndPromotionPlanningFetchAPI<{
                result: {
                    data: ForecastReview[];
                },
                slicers?: ForecastReviewDropdownModel;
            }>> = await fetchData("/forecast-review", {
                method: "POST",
                data: {
                    customer_id: globalCustomer.id,
                    ...selectedSlicers
                }
            });

            toast.dismiss({
                id: fetchingDataToastID,
                containerId: TOAST_CONTAINER_ID
            });

            if (fetchAPIResponse.data.data) {
                updateGridDataAndSlicers(fetchAPIResponse.data.data);
                dispatchFn(tradeAndPromoPlanningTabActions.setTabApiDataFetched({
                    [FORECAST_REVIEW_TAB_ID]: true
                }));

            } else {
                toast.error('Error in fetching Forecast Review data', {
                    containerId: TOAST_CONTAINER_ID,
                    ...getToastOptions('error')
                });
            }

        } catch (error: Error | any) {
            console.error("Error ", error)
            console.error(`Request Error: ${error}`);
            toast.dismiss({
                id: fetchingDataToastID,
                containerId: TOAST_CONTAINER_ID
            });
            toast.error((
                error?.response?.data?.error ||
                'Error in fetching Forecast Review data'
            ), {
                containerId: TOAST_CONTAINER_ID,
                ...getToastOptions('error')
            });
        }
    }, [globalCustomer.id, selectedSlicers, dispatchFn, updateGridDataAndSlicers, fetchData]);

    useEffect(() => {
        if (
            !tabApiDataFetched &&
            activeSubTabId === FORECAST_REVIEW_TAB_ID
        ) {
            fetchForecastReviewData();
        }
    }, [tabApiDataFetched, activeSubTabId, fetchForecastReviewData]);

    return (
        <>
            <div className="row">
                <div className="col col-12">
                    <ForecastReviewSlicers />
                </div>
            </div>

            <div className="ag-theme-balham mt-2" style={{ height: props.gridHeight, maxHeight: props.gridHeight, width: '100%', overflowX: 'auto', overflowY: 'auto' }}>
                <AgGridReact
                    ref={agGridRef}
                    rowData={gridRowDataCopy}
                    columnDefs={gridColDef}
                    gridOptions={{
                        ...(AG_GRID_OPTIONS as any),
                        rowHeight: 22,
                        suppressMenuHide: true
                    }}
                    defaultColDef={{
                        ...AG_GRID_DEFAULT_COL_DEF,
                        wrapText: true,
                        autoHeight: true,
                    }}
                    modules={[
                        ...AG_GRID_MODULES,
                        ColumnsToolPanelModule,
                        FiltersToolPanelModule,
                        SetFilterModule
                    ]}
                />
            </div>
        </>
    );
};

export default ForecastReviewGrid;
